import { useEffect, useState } from "react";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { ArrowRight, TrendingUp, Zap, Layers } from "lucide-react";
import { useTranslation } from "react-i18next";
import { REGISTER_URL } from "@/data/site";
import api from "@/lib/api";

export default function Hero() {
  const { t, i18n } = useTranslation();
  const translated = {
    hero_pill: t("hero.pill"),
    hero_headline_line1: t("hero.headline1"),
    hero_headline_line2: t("hero.headline2"),
    hero_subline: t("hero.subline"),
  };
  const [cms, setCms] = useState({});
  useEffect(() => {
    api.get("/content").then(({ data }) => setCms(data || {})).catch(() => {});
  }, []);
  // Use CMS overrides only when language is English (CMS content is admin-edited English).
  const content = i18n.language === "en" ? { ...translated, ...cms } : translated;
  // Mouse-parallax for the radial glow
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const smx = useSpring(mx, { stiffness: 80, damping: 18 });
  const smy = useSpring(my, { stiffness: 80, damping: 18 });
  const glowX = useTransform(smx, (v) => v * 40);
  const glowY = useTransform(smy, (v) => v * 40);

  useEffect(() => {
    const onMove = (e) => {
      const w = window.innerWidth, h = window.innerHeight;
      mx.set((e.clientX / w) * 2 - 1);
      my.set((e.clientY / h) * 2 - 1);
    };
    window.addEventListener("mousemove", onMove);
    return () => window.removeEventListener("mousemove", onMove);
  }, [mx, my]);

  return (
    <section
      data-testid="hero-section"
      className="relative min-h-[92vh] flex items-center justify-center overflow-hidden pt-32 pb-16"
    >
      {/* Background layers — global MatrixBackground handles the site-wide watermark.
          Static bg-grid pattern removed so the matrix has full canvas. */}

      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-brand/60 to-transparent" />

      {/* Floating orbs — purely decorative, position is a deterministic
          function of `i` so using a position-based key is stable across
          renders (no real list mutation happens here). */}
      {[...Array(6)].map((_, i) => (
        <motion.div
          key={`orb-${i * 14}-${(i % 3) * 25}`}
          className="absolute w-1 h-1 rounded-full bg-brand/60 pointer-events-none"
          style={{
            left: `${15 + i * 14}%`,
            top: `${20 + (i % 3) * 25}%`,
          }}
          animate={{ y: [0, -20, 0], opacity: [0.2, 0.8, 0.2] }}
          transition={{ duration: 4 + i, repeat: Infinity, delay: i * 0.4, ease: "easeInOut" }}
        />
      ))}

      <div className="relative max-w-7xl mx-auto px-5 lg:px-8 text-center w-full">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="inline-flex items-center gap-2 mb-10 pill-mono"
          data-testid="hero-pill"
        >
          {content.hero_pill}
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.05 }}
          className="font-display text-5xl sm:text-6xl lg:text-7xl xl:text-8xl font-medium leading-[0.98] tracking-[-0.025em] mb-8 max-w-5xl mx-auto"
        >
          {content.hero_headline_line1}<br />
          <motion.span
            className="text-gradient-brand font-semibold inline-block"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.25 }}
          >
            {content.hero_headline_line2}
          </motion.span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.35 }}
          className="text-[color:var(--text-2)] text-base sm:text-lg lg:text-xl max-w-3xl mx-auto mb-12 leading-relaxed font-light"
        >
          {content.hero_subline}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.5 }}
          className="flex flex-col sm:flex-row items-center justify-center gap-3 mb-16"
        >
          <motion.a
            href="#challenges"
            data-testid="hero-cta-challenges"
            className="btn-solid-brand text-sm inline-flex items-center justify-center gap-2"
            style={{ height: "48px", width: "200px", padding: "0 1.5rem", lineHeight: 1 }}
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
          >
            {t("hero.ctaPrimary")} <ArrowRight size={16} />
          </motion.a>
          <motion.a
            href={REGISTER_URL}
            target="_blank"
            rel="noreferrer"
            data-testid="hero-cta-register"
            className="btn-ghost-gradient text-sm inline-flex items-center justify-center"
            style={{ height: "48px", width: "200px", padding: "0 1.5rem", lineHeight: 1 }}
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
          >
            {t("hero.ctaSecondary")}
          </motion.a>
        </motion.div>

        <motion.div
          initial="hidden"
          animate="visible"
          variants={{
            hidden: {},
            visible: { transition: { staggerChildren: 0.12, delayChildren: 0.7 } },
          }}
          className="grid grid-cols-2 lg:grid-cols-4 gap-3 max-w-7xl mx-auto"
        >
          {[
            { icon: Zap, label: t("hero.stat1"), desc: t("hero.stat1Desc") },
            { icon: TrendingUp, label: t("hero.stat2"), desc: t("hero.stat2Desc") },
            { icon: Zap,         label: t("hero.stat3"), desc: t("hero.stat3Desc") },
            { icon: Layers, label: t("hero.stat4", "4 Platforms"), desc: t("hero.stat4Desc", "cTrader · DXTrade · MatchTrader · GooeyPro") },
          ].map((item) => (
            <motion.div
              key={item.label}
              variants={{ hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0 } }}
              whileHover={{ y: -4 }}
              transition={{ type: "spring", stiffness: 300 }}
              className="card-beam card-beam-glow p-4 sm:p-5 flex flex-col sm:flex-row gap-3 items-center sm:items-start text-center sm:text-left"
            >
              <div className="w-11 h-11 sm:w-12 sm:h-12 rounded-lg bg-brand/10 border border-brand/30 grid place-items-center shrink-0">
                <item.icon className="text-brand" size={20} />
              </div>
              <div className="min-w-0">
                <div className="font-display text-white text-sm sm:text-base font-medium leading-tight">{item.label}</div>
                <div className="text-zinc-500 text-[11px] sm:text-xs mt-1 font-light leading-snug">{item.desc}</div>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
